import React, { useState, useRef, useEffect, useCallback } from "react";
import { INSTRUMENTS, TIMEFRAMES, BASE_PRICES, fmt } from "./constants";


// ─── WATCHLIST PRICE SIMULATOR ────────────────────────────────
const useSimPrice = (symbol) => {
  const base = BASE_PRICES[symbol] || 100;
  const [price, setPrice] = React.useState(base + (Math.random()-0.5)*base*0.001);
  const [chg, setChg] = React.useState((Math.random()-0.5)*base*0.005);
  const open = React.useRef(base);
  React.useEffect(() => {
    const iv = setInterval(() => {
      setPrice(p => {
        const vol = (INSTRUMENTS[symbol]?.tickSize || 0.25) * 3;
        const np = p + (Math.random()-0.49) * vol;
        setChg(np - open.current);
        return np;
      });
    }, 1200 + Math.random()*800);
    return () => clearInterval(iv);
  }, [symbol]);
  return { price, chg, pct: (chg / open.current) * 100 };
};

const WatchlistItem = ({sym,active,onSelect}) => {
  const {price,chg,pct} = useSimPrice(sym);
  const up = chg>=0;
  return (
    <div onClick={()=>onSelect(sym)} style={{
      padding:"5px 8px", borderBottom:"1px solid rgba(255,255,255,0.04)", cursor:"pointer",
      background:active?"rgba(0,200,150,0.08)":"transparent",
      borderLeft:active?"2px solid #00C896":"2px solid transparent",
    }}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontFamily:"monospace",fontSize:11,fontWeight:600,color:active?"#00C896":"#e8e8e8"}}>{sym}</span>
        <span style={{fontFamily:"monospace",fontSize:10,color:up?"#00C896":"#FF3B3B"}}>{fmt.price(price,sym)}</span>
      </div>
      <div style={{display:"flex",justifyContent:"space-between"}}>
        <span style={{fontSize:8,color:"#333",fontFamily:"monospace"}}>{INSTRUMENTS[sym]?.exchange}</span>
        <span style={{fontFamily:"monospace",fontSize:9,color:up?"#00C896":"#FF3B3B"}}>{up?"+":""}{pct.toFixed(2)}%</span>
      </div>
    </div>
  );
};


// ─── CLOCK / PRICE DISPLAY ────────────────────────────────────
const Clock = () => {
  const [t,setT]=useState(fmt.time());
  useEffect(()=>{const iv=setInterval(()=>setT(fmt.time()),1000);return()=>clearInterval(iv);},[]);
  return <span>{t} ET</span>;
};
const PriceDisplay = ({symbol,priceRef}) => {
  const [price,setPrice]=useState(BASE_PRICES[symbol]||0);
  const [prev,setPrev]=useState(price);
  useEffect(()=>{const iv=setInterval(()=>{const c=priceRef.current;if(c&&c!==price){setPrev(price);setPrice(c);}},200);return()=>clearInterval(iv);},[price]);
  const up=price>=prev;
  return <span style={{color:up?"#00C896":"#FF3B3B",fontFamily:"monospace",fontSize:15,fontWeight:700}}>
    {fmt.price(price,symbol)}<span style={{fontSize:10,marginLeft:6}}>{up?"▲":"▼"} {Math.abs(price-prev).toFixed(2)}</span>
  </span>;
};


// ─── PERFORMANCE CALENDAR ────────────────────────────────────
const PerformanceCalendar = ({ history, onClose }) => {
  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());

  // Build daily P&L map from trade history
  const dailyPnl = {};
  (history||[]).forEach(t => {
    if(!t.closedAt && !t.exitTime) return;
    const d = new Date(t.closedAt || t.exitTime);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    dailyPnl[key] = (dailyPnl[key]||0) + Number(t.pnl||0);
  });

  const daysInMonth = new Date(viewYear, viewMonth+1, 0).getDate();
  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const monthName   = new Date(viewYear, viewMonth).toLocaleString('default',{month:'long'});

  // Yearly heatmap data
  const yearDays = [];
  for(let m=0; m<12; m++){
    const dim = new Date(viewYear, m+1, 0).getDate();
    for(let d=1; d<=dim; d++){
      const key = `${viewYear}-${m}-${d}`;
      yearDays.push({ m, d, pnl: dailyPnl[key]||0, key });
    }
  }
  const maxAbs = Math.max(1, ...yearDays.map(x=>Math.abs(x.pnl)));

  const totalMonth = Array.from({length:daysInMonth},(_,i)=>{
    const key=`${viewYear}-${viewMonth}-${i+1}`;
    return dailyPnl[key]||0;
  }).reduce((a,b)=>a+b,0);

  const tradingDays = Object.keys(dailyPnl).filter(k=>{
    const [y,m]=k.split('-').map(Number);
    return y===viewYear && m===viewMonth;
  }).length;

  const winDays = Object.entries(dailyPnl).filter(([k,v])=>{
    const [y,m]=k.split('-').map(Number);
    return y===viewYear && m===viewMonth && v>0;
  }).length;

  return (
    <div style={{width:340,height:"100%",background:"#0d0f14",borderLeft:"1px solid #1e2130",display:"flex",flexDirection:"column",flexShrink:0}}>
      {/* Header */}
      <div style={{padding:"0 14px",height:38,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #1e2130",flexShrink:0}}>
        <span style={{fontSize:12,fontWeight:600,color:"#a0a5b4",letterSpacing:"0.05em"}}>Performance</span>
        <button onClick={onClose} style={{background:"none",border:"none",color:"#4a4f5e",cursor:"pointer",fontSize:16,lineHeight:1,padding:"0 2px"}}>✕</button>
      </div>

      <div style={{flex:1,overflowY:"auto",padding:"12px"}}>

        {/* Month nav */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
          <button onClick={()=>{if(viewMonth===0){setViewMonth(11);setViewYear(y=>y-1);}else setViewMonth(m=>m-1);}}
            style={{background:"#1a1d27",border:"1px solid #2a2d3a",color:"#a0a5b4",borderRadius:4,width:26,height:26,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>‹</button>
          <span style={{fontSize:13,fontWeight:600,color:"#e2e4e9"}}>{monthName} {viewYear}</span>
          <button onClick={()=>{if(viewMonth===11){setViewMonth(0);setViewYear(y=>y+1);}else setViewMonth(m=>m+1);}}
            style={{background:"#1a1d27",border:"1px solid #2a2d3a",color:"#a0a5b4",borderRadius:4,width:26,height:26,cursor:"pointer",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center"}}>›</button>
        </div>

        {/* Month stats */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,marginBottom:12}}>
          {[
            {l:"Month P&L", v:(totalMonth>=0?"+":"")+fmt.usd(totalMonth), c:totalMonth>=0?"#00c896":"#f03b3b"},
            {l:"Trade Days", v:tradingDays, c:"#e2e4e9"},
            {l:"Win Rate",   v:tradingDays>0?Math.round(winDays/tradingDays*100)+"%":"—", c:"#00c896"},
          ].map(s=>(
            <div key={s.l} style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"7px 8px"}}>
              <div style={{fontSize:9,color:"#4a4f5e",marginBottom:3,letterSpacing:"0.06em",textTransform:"uppercase"}}>{s.l}</div>
              <div style={{fontSize:12,fontWeight:700,color:s.c,fontFamily:"monospace"}}>{s.v}</div>
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:6,padding:"10px",marginBottom:12}}>
          {/* Day headers */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4}}>
            {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d=>(
              <div key={d} style={{textAlign:"center",fontSize:9,color:"#3a3d4a",fontWeight:600,padding:"2px 0"}}>{d}</div>
            ))}
          </div>
          {/* Day cells */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {Array(firstDay).fill(null).map((_,i)=>(
              <div key={"e"+i}/>
            ))}
            {Array.from({length:daysInMonth},(_,i)=>{
              const day = i+1;
              const key = `${viewYear}-${viewMonth}-${day}`;
              const pnl = dailyPnl[key];
              const isToday = day===now.getDate() && viewMonth===now.getMonth() && viewYear===now.getFullYear();
              const hasTrade = pnl !== undefined;
              return (
                <div key={day} title={hasTrade?`${fmt.usd(pnl)}`:"No trades"} style={{
                  aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                  borderRadius:3,cursor:hasTrade?"pointer":"default",
                  background: hasTrade ? (pnl>=0?"rgba(0,200,150,0.15)":"rgba(240,59,59,0.15)") : "transparent",
                  border: isToday ? "1px solid #00c896" : "1px solid transparent",
                  transition:"all 0.1s",
                }}>
                  <span style={{fontSize:9,color:hasTrade?(pnl>=0?"#00c896":"#f03b3b"):"#3a3d4a",fontWeight:isToday?700:400}}>{day}</span>
                  {hasTrade&&<span style={{fontSize:7,color:pnl>=0?"#00c896":"#f03b3b",fontFamily:"monospace",marginTop:1,lineHeight:1}}>{pnl>=0?"+":""}{pnl>=1000?fmt.usd(pnl/1000)+"k":fmt.usd(pnl)}</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Year heatmap */}
        <div style={{marginBottom:12}}>
          <div style={{fontSize:10,fontWeight:600,color:"#5a5f6e",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>{viewYear} Heatmap</div>
          <div style={{display:"flex",gap:2,flexWrap:"wrap"}}>
            {Array.from({length:12},(_,m)=>{
              const dim=new Date(viewYear,m+1,0).getDate();
              const monthPnl=Array.from({length:dim},(__,d)=>dailyPnl[`${viewYear}-${m}-${d+1}`]||0).reduce((a,b)=>a+b,0);
              const mn=new Date(viewYear,m).toLocaleString('default',{month:'short'});
              return (
                <div key={m} onClick={()=>setViewMonth(m)}
                  title={`${mn}: ${fmt.usd(monthPnl)}`}
                  style={{
                    flex:1,minWidth:18,height:32,borderRadius:3,cursor:"pointer",
                    background: monthPnl===0?"#1a1d27":monthPnl>0?"rgba(0,200,150,0.2)":"rgba(240,59,59,0.2)",
                    border: viewMonth===m?"1px solid #00c896":"1px solid transparent",
                    display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,
                  }}>
                  <span style={{fontSize:7,color:"#4a4f5e"}}>{mn}</span>
                  {monthPnl!==0&&<span style={{fontSize:6,color:monthPnl>0?"#00c896":"#f03b3b",fontFamily:"monospace"}}>{monthPnl>0?"+":""}{Math.round(monthPnl/1000)}k</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent trade days list */}
        <div>
          <div style={{fontSize:10,fontWeight:600,color:"#5a5f6e",letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Recent Days</div>
          {Object.entries(dailyPnl).sort((a,b)=>b[0].localeCompare(a[0])).slice(0,8).map(([key,pnl])=>{
            const [y,m,d]=key.split('-').map(Number);
            const date=new Date(y,m,d);
            return (
              <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid #1a1d27"}}>
                <span style={{fontSize:11,color:"#7a7f8e"}}>{date.toLocaleDateString('default',{month:'short',day:'numeric',year:'numeric'})}</span>
                <span style={{fontSize:11,fontFamily:"monospace",fontWeight:700,color:pnl>=0?"#00c896":"#f03b3b"}}>{pnl>=0?"+":""}{fmt.usd(pnl)}</span>
              </div>
            );
          })}
          {Object.keys(dailyPnl).length===0&&<div style={{fontSize:11,color:"#3a3d4a",textAlign:"center",padding:"12px 0"}}>No trade history yet</div>}
        </div>

      </div>
    </div>
  );
};

// ─── BRACKET ORDER PANEL ─────────────────────────────────────
const BracketOrderPanel = ({ inst, bid, ask, qty, symbol }) => {
  const [enabled, setEnabled] = useState(false);
  const [slTicks, setSlTicks] = useState(10);
  const [tpTicks, setTpTicks] = useState(20);
  const tickVal = inst.tickVal;
  const tickSize = inst.tickSize;
  const slRisk = slTicks * tickVal * qty;
  const tpReward = tpTicks * tickVal * qty;
  const rr = slTicks > 0 ? (tpTicks / slTicks).toFixed(1) : "—";

  return (
    <div style={{marginBottom:8}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:enabled?6:0}}>
        <div style={{fontSize:9,fontWeight:600,color:"#4a4f5e",letterSpacing:"0.1em",textTransform:"uppercase"}}>Bracket (SL/TP)</div>
        <div onClick={()=>setEnabled(e=>!e)} style={{
          width:28,height:15,borderRadius:8,background:enabled?"#00c896":"#2a2d3a",
          position:"relative",cursor:"pointer",transition:"background 0.2s",flexShrink:0,
        }}>
          <div style={{position:"absolute",top:2,left:enabled?13:2,width:11,height:11,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
        </div>
      </div>
      {enabled&&(
        <div style={{background:"#111318",border:"1px solid #1e2130",borderRadius:4,padding:"8px 10px"}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6,marginBottom:6}}>
            <div>
              <div style={{fontSize:9,color:"#f03b3b",fontWeight:600,letterSpacing:"0.08em",marginBottom:3}}>SL TICKS</div>
              <div style={{display:"flex",alignItems:"center",background:"#1a1d27",border:"1px solid rgba(240,59,59,0.3)",borderRadius:3,overflow:"hidden"}}>
                <button onClick={()=>setSlTicks(s=>Math.max(1,s-1))} style={{width:22,background:"none",border:"none",color:"#f03b3b",cursor:"pointer",fontSize:14,fontWeight:300}}>−</button>
                <input type="number" value={slTicks} min={1} onChange={e=>setSlTicks(Math.max(1,parseInt(e.target.value)||1))}
                  style={{flex:1,background:"none",border:"none",color:"#f03b3b",fontSize:12,fontWeight:700,textAlign:"center",fontFamily:"monospace",width:0}}/>
                <button onClick={()=>setSlTicks(s=>s+1)} style={{width:22,background:"none",border:"none",color:"#f03b3b",cursor:"pointer",fontSize:14,fontWeight:300}}>+</button>
              </div>
              <div style={{fontSize:9,color:"#f03b3b",textAlign:"center",marginTop:2,fontFamily:"monospace"}}>-${slRisk.toFixed(0)}</div>
            </div>
            <div>
              <div style={{fontSize:9,color:"#00c896",fontWeight:600,letterSpacing:"0.08em",marginBottom:3}}>TP TICKS</div>
              <div style={{display:"flex",alignItems:"center",background:"#1a1d27",border:"1px solid rgba(0,200,150,0.3)",borderRadius:3,overflow:"hidden"}}>
                <button onClick={()=>setTpTicks(s=>Math.max(1,s-1))} style={{width:22,background:"none",border:"none",color:"#00c896",cursor:"pointer",fontSize:14,fontWeight:300}}>−</button>
                <input type="number" value={tpTicks} min={1} onChange={e=>setTpTicks(Math.max(1,parseInt(e.target.value)||1))}
                  style={{flex:1,background:"none",border:"none",color:"#00c896",fontSize:12,fontWeight:700,textAlign:"center",fontFamily:"monospace",width:0}}/>
                <button onClick={()=>setTpTicks(s=>s+1)} style={{width:22,background:"none",border:"none",color:"#00c896",cursor:"pointer",fontSize:14,fontWeight:300}}>+</button>
              </div>
              <div style={{fontSize:9,color:"#00c896",textAlign:"center",marginTop:2,fontFamily:"monospace"}}>+${tpReward.toFixed(0)}</div>
            </div>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",borderTop:"1px solid #1e2130",paddingTop:5}}>
            <span style={{fontSize:9,color:"#4a4f5e"}}>R:R Ratio</span>
            <span style={{fontSize:10,fontFamily:"monospace",fontWeight:700,color:parseFloat(rr)>=2?"#00c896":parseFloat(rr)>=1?"#f5a623":"#f03b3b"}}>{rr}R</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── DOM PRICE LADDER ────────────────────────────────────────
const DOMPanel = ({ symbol, bid, ask, livePrice, inst, qty, onOrder }) => {
  const cur = livePrice?.current || bid || 100;
  const tickSize = inst?.tickSize || 0.25;
  const tickVal  = inst?.tickVal  || 12.5;
  // Generate 12 levels above and below current price
  const levels = [];
  for (let i = 12; i >= -12; i--) {
    levels.push(+(cur + i * tickSize).toFixed(6));
  }
  // Fake depth — asymmetric around spread
  const fakeDepth = (price) => {
    const dist = Math.abs(price - cur) / tickSize;
    if (dist > 8) return 0;
    const base = Math.floor(80 - dist * 8 + Math.sin(price * 17.3) * 12);
    return Math.max(0, base);
  };
  const maxDepth = 80;

  return (
    <div style={{width:200,height:"100%",background:"#0d0f14",borderLeft:"1px solid #1e2130",display:"flex",flexDirection:"column",flexShrink:0,fontFamily:"monospace"}}>
      {/* Header */}
      <div style={{padding:"0 10px",height:38,display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid #1e2130",flexShrink:0}}>
        <span style={{fontSize:11,fontWeight:700,color:"#a0a5b4"}}>{symbol} DOM</span>
        <span style={{fontSize:9,color:"#4a4f5e"}}>{inst?.exchange}</span>
      </div>
      {/* Column headers */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 56px 1fr",fontSize:8,color:"#3a3d4a",padding:"3px 6px",borderBottom:"1px solid #1e2130",textTransform:"uppercase",letterSpacing:"0.08em",flexShrink:0}}>
        <span style={{textAlign:"right",paddingRight:4}}>Bid</span>
        <span style={{textAlign:"center"}}>Price</span>
        <span style={{paddingLeft:4}}>Ask</span>
      </div>
      {/* Ladder */}
      <div style={{flex:1,overflowY:"auto",overflowX:"hidden"}}>
        {levels.map((price, i) => {
          const isBid = price < cur;
          const isAsk = price > cur;
          const isMid = Math.abs(price - cur) < tickSize * 0.5;
          const depth = fakeDepth(price);
          const depthPct = depth / maxDepth * 100;
          const priceStr = price.toFixed(inst?.tickSize < 0.1 ? 3 : 2);
          return (
            <div key={i} onClick={() => onOrder(isBid||isMid ? 'buy' : 'sell')}
              style={{
                display:"grid", gridTemplateColumns:"1fr 56px 1fr",
                height:20, alignItems:"center", cursor:"pointer", position:"relative",
                background: isMid ? "rgba(255,255,255,0.04)" : "transparent",
                borderTop: isMid ? "1px solid rgba(255,255,255,0.08)" : undefined,
                borderBottom: isMid ? "1px solid rgba(255,255,255,0.08)" : undefined,
              }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.04)"}
              onMouseLeave={e=>e.currentTarget.style.background=isMid?"rgba(255,255,255,0.04)":"transparent"}>
              {/* Bid side */}
              <div style={{position:"relative",height:"100%",display:"flex",alignItems:"center",justifyContent:"flex-end",paddingRight:6}}>
                {isBid && depth > 0 && (
                  <div style={{position:"absolute",right:0,top:0,height:"100%",width:`${depthPct}%`,background:"rgba(0,200,150,0.15)",pointerEvents:"none"}}/>
                )}
                <span style={{fontSize:10,color:isBid?"#00c896":"transparent",fontWeight:isBid?600:400,position:"relative",zIndex:1}}>
                  {isBid ? depth : ""}
                </span>
              </div>
              {/* Price */}
              <div style={{textAlign:"center",fontSize:10,fontWeight:700,
                color:isMid?"#e2e4e9":isAsk?"#f03b3b":isBid?"#00c896":"#7a7f8e",
                background:isMid?"rgba(255,255,255,0.05)":undefined,
                padding:"0 2px", letterSpacing:"0.02em"}}>
                {priceStr}
              </div>
              {/* Ask side */}
              <div style={{position:"relative",height:"100%",display:"flex",alignItems:"center",paddingLeft:6}}>
                {isAsk && depth > 0 && (
                  <div style={{position:"absolute",left:0,top:0,height:"100%",width:`${depthPct}%`,background:"rgba(240,59,59,0.15)",pointerEvents:"none"}}/>
                )}
                <span style={{fontSize:10,color:isAsk?"#f03b3b":"transparent",fontWeight:isAsk?600:400,position:"relative",zIndex:1}}>
                  {isAsk ? depth : ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer — quick trade */}
      <div style={{borderTop:"1px solid #1e2130",padding:"8px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,flexShrink:0}}>
        <button onClick={()=>onOrder('buy')} style={{padding:"7px 4px",background:"rgba(0,200,150,0.12)",border:"1px solid rgba(0,200,150,0.3)",
          color:"#00c896",borderRadius:3,fontSize:10,fontWeight:700,cursor:"pointer",textTransform:"uppercase",letterSpacing:"0.05em"}}>
          BUY {qty}
        </button>
        <button onClick={()=>onOrder('sell')} style={{padding:"7px 4px",background:"rgba(240,59,59,0.12)",border:"1px solid rgba(240,59,59,0.3)",
          color:"#f03b3b",borderRadius:3,fontSize:10,fontWeight:700,cursor:"pointer",textTransform:"uppercase",letterSpacing:"0.05em"}}>
          SELL {qty}
        </button>
      </div>
    </div>
  );
};

// ─── CANDLE TIMER — countdown to next bar close ──────────────
const TF_SECONDS = {"1m":60,"2m":120,"3m":180,"5m":300,"10m":600,"15m":900,"30m":1800,"1h":3600,"2h":7200,"4h":14400,"6h":21600,"8h":28800,"12h":43200,"1D":86400,"3D":259200,"1W":604800};
const CandleTimer = ({ timeframe }) => {
  const [rem, setRem] = useState(0);
  useEffect(() => {
    const calc = () => {
      const secs = TF_SECONDS[timeframe] || 60;
      const now = Math.floor(Date.now() / 1000);
      const r = secs - (now % secs);
      setRem(r);
    };
    calc();
    const iv = setInterval(calc, 1000);
    return () => clearInterval(iv);
  }, [timeframe]);
  const h = Math.floor(rem/3600);
  const m = Math.floor((rem%3600)/60);
  const s = rem%60;
  const frac = rem / (TF_SECONDS[timeframe]||60);
  const urgent = frac < 0.1;
  return (
    <div style={{display:"flex",alignItems:"center",gap:4,padding:"0 6px",borderLeft:"1px solid #1e2130",height:26}}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={urgent?"#f03b3b":"#3a3d4a"} strokeWidth="2">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
      <span style={{fontSize:9,fontFamily:"monospace",color:urgent?"#f03b3b":"#3a3d4a",fontWeight:urgent?700:400}}>
        {h>0?`${h}:${String(m).padStart(2,'0')}:`:""}
        {h>0?String(s).padStart(2,'0'):`${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`}
      </span>
    </div>
  );
};

// ─── OHLC BAR — reads live candle from DOM event ─────────────
const OHLCBar = ({ symbol, priceRef, settings }) => {
  const [ohlc, setOhlc] = useState(null);
  useEffect(() => {
    const h = (e) => setOhlc(e.detail);
    window.addEventListener('chart:ohlc', h);
    return () => window.removeEventListener('chart:ohlc', h);
  }, []);
  const p = fmt.price;
  if (!ohlc) return (
    <div style={{display:"flex",gap:8,fontSize:10,fontFamily:"monospace",color:"#4a4f5e"}}>
      <span style={{color:"#3a3d4a"}}>{symbol}</span>
      <span>Price <span style={{color:"#a0a5b4"}}>{p(priceRef?.current, symbol)}</span></span>
    </div>
  );
  const up = ohlc.c >= ohlc.o;
  const chg = ohlc.c - ohlc.o;
  const chgPct = ohlc.o > 0 ? (chg/ohlc.o*100) : 0;
  return (
    <div style={{display:"flex",gap:10,fontSize:10,fontFamily:"monospace",alignItems:"center"}}>
      {[
        ["O", ohlc.o, "#888"],
        ["H", ohlc.h, settings.upColor],
        ["L", ohlc.l, settings.downColor],
        ["C", ohlc.c, up ? settings.upColor : settings.downColor],
      ].map(([l, v, c]) => (
        <span key={l} style={{color:"#3a3d4a"}}>{l}<span style={{color:c,marginLeft:3}}>{p(v,symbol)}</span></span>
      ))}
      <span style={{color:up?"#00c896":"#f03b3b",marginLeft:2}}>{up?"+":""}{p(chg,symbol)} ({up?"+":""}{chgPct.toFixed(2)}%)</span>
      {settings.showVolInStatus&&ohlc.vol!=null&&(
        <span style={{color:"#3a3d4a"}}>Vol<span style={{color:"#5a5f6e",marginLeft:3}}>{(ohlc.vol*1000).toFixed(0)}</span></span>
      )}
    </div>
  );
};

// ─── RSI PANEL ───────────────────────────────────────────────
const RSIPanel = ({ symbol, indicators }) => {
  // RSI is rendered as a separate canvas below main chart
  // The main chart passes candle data via event
  const canvasRef = useRef(null);
  const [rsiData, setRsiData] = useState([]);

  useEffect(() => {
    const h = (e) => {
      if (e.detail?.rsi) setRsiData(e.detail.rsi);
    };
    window.addEventListener('chart:rsi', h);
    return () => window.removeEventListener('chart:rsi', h);
  }, []);

  const rsiInd = indicators.find(i => i.type === 'RSI' && i.visible);
  const H = 72, periods = rsiData.length;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rsiData.length) return;
    const ctx = canvas.getContext('2d');
    const DPR = window.devicePixelRatio || 1;
    const W = canvas.width / DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.fillStyle = '#0d0f14';
    ctx.fillRect(0, 0, W, H);

    // Grid lines at 30, 50, 70
    [30, 50, 70].forEach(level => {
      const y = H - (level / 100) * H;
      ctx.beginPath();
      ctx.strokeStyle = level === 50 ? '#2a2d3a' : '#1e2130';
      ctx.lineWidth = 1;
      ctx.setLineDash(level === 50 ? [] : [4, 4]);
      ctx.moveTo(0, y); ctx.lineTo(W, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#3a3d4a';
      ctx.font = '8px monospace';
      ctx.textAlign = 'right';
      ctx.fillText(level, W - 4, y - 2);
    });

    // Overbought/oversold fills
    ctx.fillStyle = 'rgba(0,200,150,0.06)';
    ctx.fillRect(0, 0, W, H - (70/100)*H);
    ctx.fillStyle = 'rgba(240,59,59,0.06)';
    ctx.fillRect(0, H-(30/100)*H, W, (30/100)*H);

    // RSI line
    const col = rsiInd?.color || '#a78bfa';
    ctx.beginPath();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.5;
    rsiData.forEach((v, i) => {
      if (v == null) return;
      const x = (i / (rsiData.length - 1)) * W;
      const y = H - (v / 100) * H;
      if (i === 0 || rsiData[i-1] == null) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Current RSI value
    const last = rsiData[rsiData.length - 1];
    if (last != null) {
      ctx.fillStyle = last > 70 ? '#00c896' : last < 30 ? '#f03b3b' : col;
      ctx.font = 'bold 9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`RSI(${rsiInd?.period||14}) ${last.toFixed(1)}`, 4, 12);
    }
  }, [rsiData]);

  return (
    <div style={{ flexShrink:0, borderTop:'1px solid #1e2130', position:'relative' }}>
      <canvas ref={canvasRef} style={{ width:'100%', height:H, display:'block' }}
        width={800} height={H * (window.devicePixelRatio||1)}/>
    </div>
  );
};

// ─── ADD INDICATOR FORM ──────────────────────────────────────
const INDICATOR_COLORS = ["#00c896","#f5a623","#4db8ff","#a78bfa","#f03b3b","#ff9800","#26a69a","#ef5350"];
const AddIndicatorForm = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("EMA");
  const [period, setPeriod] = useState(20);
  const [color, setColor] = useState("#ff9800");
  if (!open) return (
    <button onClick={()=>setOpen(true)} style={{padding:"3px 8px",background:"none",border:"1px dashed #2a2d3a",color:"#4a4f5e",borderRadius:3,fontSize:10,cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
      <span style={{fontSize:12,lineHeight:1}}>+</span> Add
    </button>
  );
  return (
    <div style={{display:"flex",alignItems:"center",gap:4,background:"#1a1d27",border:"1px solid #2a2d3a",borderRadius:4,padding:"3px 6px"}}>
      <select value={type} onChange={e=>setType(e.target.value)} style={{background:"transparent",border:"none",color:"#e2e4e9",fontSize:10,cursor:"pointer",outline:"none"}}>
        {["EMA","SMA","VWAP","BB","RSI"].map(t=><option key={t} value={t}>{t}</option>)}
      </select>
      <input type="number" value={period} min={2} max={500} onChange={e=>setPeriod(parseInt(e.target.value)||20)}
        style={{width:36,background:"transparent",border:"none",color:"#e2e4e9",fontSize:10,fontFamily:"monospace",outline:"none",textAlign:"center"}}/>
      <div style={{display:"flex",gap:2}}>
        {INDICATOR_COLORS.map(c=>(
          <div key={c} onClick={()=>setColor(c)} style={{width:10,height:10,borderRadius:2,background:c,cursor:"pointer",
            border:color===c?"1.5px solid #fff":"1px solid transparent",flexShrink:0}}/>
        ))}
      </div>
      <button onClick={()=>{onAdd({type,period,color,dash:type==="SMA"});setOpen(false);}}
        style={{padding:"2px 8px",background:"rgba(0,200,150,0.15)",border:"1px solid rgba(0,200,150,0.4)",color:"#00c896",borderRadius:3,fontSize:10,fontWeight:600,cursor:"pointer"}}>Add</button>
      <button onClick={()=>setOpen(false)} style={{background:"none",border:"none",color:"#4a4f5e",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 2px"}}>✕</button>
    </div>
  );
};

// ─── MAIN TERMINAL ────────────────────────────────────────────

export { WatchlistItem, Clock, PriceDisplay, PerformanceCalendar, BracketOrderPanel, DOMPanel, CandleTimer, OHLCBar, RSIPanel, AddIndicatorForm };
